/*
 * Professional Paint PDP inline configurator.
 *
 * Presentation is dedicated to Professional Paint, while the theme's shared
 * touch-up engine remains authoritative for color matching, mapped products,
 * variant IDs, price, availability, VIN properties, and cart submission.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-prof-configurator]';
  var CONTENT_SELECTOR = '[data-prof-configurator-content]';
  var DATA_RETRY_LIMIT = 48;
  var DATA_RETRY_DELAY = 250;
  var SIZE_ORDER = ['Half Pint', 'Pint', 'Quart', 'Gallon'];

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  function normalizeVin(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .replace(/[IOQ]/g, '')
      .slice(0, 17);
  }

  function normalizePlate(value) {
    return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
  }

  function formatMoney(value) {
    var amount = Number(String(value == null ? '' : value).replace(/,/g, ''));
    return isNaN(amount) ? '' : '$' + amount.toFixed(2);
  }

  function isVinMatch(code) {
    return /^(?:\.vin|vin)$|match\s+(?:paint\s+)?by\s+vin/i.test(String(code || '').trim());
  }

  function canonicalSize(value) {
    var wanted = normalize(value);
    return SIZE_ORDER.find(function (size) { return normalize(size) === wanted; }) || '';
  }

  function boot(root) {
    if (!root || root.__profConfiguratorBooted) return;
    const content = root.querySelector(CONTENT_SELECTOR);
    if (!content) return;
    root.__profConfiguratorBooted = true;

    var fallbackImages = {
      paintonly: root.getAttribute('data-prof-paint-only-image') || '',
      essential: root.getAttribute('data-prof-essential-image') || '',
      preferred: root.getAttribute('data-prof-preferred-image') || '',
    };
    var state = {
      method: 'vin',
      colorCode: '',
      colorDetails: null,
      selectedSize: canonicalSize(root.getAttribute('data-prof-current-size')),
      selectedPackage: String(root.getAttribute('data-prof-current-package') || '').trim(),
      vin: '',
      plate: '',
      plateState: '',
      plateMessage: '',
      resolvingPlate: false,
      userChoseMethod: false,
      resolvingVin: false,
      resolvedVin: '',
      vehicleKey: '',
      allowedCodes: null,
      allowedCodesLoading: false,
      paintDataAvailable: null,
      twoToneCodes: [],
      toneChoice: '',
      toneArea: '',
      manualVinSaved: false,
      adding: false,
    };
    var paintCodeRequiredMessage = root.getAttribute('data-prof-paint-code-required-message') || "Sorry, we couldn't decode your paint code. Please select your paint code to continue.";
    var initialSize = state.selectedSize;
    var initialPackage = state.selectedPackage;
    var makeKey = String(root.getAttribute('data-prof-make') || '').trim();
    var collectionHandle = String(root.getAttribute('data-prof-collection-handle') || '').trim();
    var catalogExpansionStarted = false;
    var renderTimer = 0;
    var dataRetryTimer = 0;
    var paintContextRequest = 0;
    const priceBlock = document.querySelector('[data-prof-price-block]');
    const startingPriceText = priceBlock
      ? String(
          priceBlock.getAttribute('data-prof-starting-price') ||
          (priceBlock.querySelector('[data-prof-price-value]') || {}).textContent ||
          ''
        ).trim()
      : '';

    function getMap() {
      return window.__touchupSizeMap && typeof window.__touchupSizeMap === 'object'
        ? window.__touchupSizeMap
        : {};
    }

    function normalizePaintCode(value) {
      return String(value || '').trim().toUpperCase();
    }

    function comparablePaintCode(value) {
      var code = normalizePaintCode(value);
      return code.indexOf('WA') === 0 ? code.slice(2) : code;
    }

    function paintCodeMatches(code, allowed) {
      var comparable = comparablePaintCode(code);
      var approved = comparablePaintCode(allowed);
      return comparable === approved || comparable.indexOf(approved + ' ') === 0 || comparable.indexOf(approved + '+') === 0;
    }

    function uniquePaintCodes(codes) {
      return (Array.isArray(codes) ? codes : []).map(normalizePaintCode).filter(function (code, index, all) {
        return code && !isVinMatch(code) && all.indexOf(code) === index;
      });
    }

    function codeForMethod() {
      if (state.method !== 'vin') return isVinMatch(state.colorCode) ? '' : state.colorCode;
      return resolvedVehiclePaintCode();
    }

    function mapKeyForCode(code) {
      var normalizedCode = String(code || '').trim().toUpperCase();
      return Object.keys(getMap()).find(function (key) {
        return String(key || '').trim().toUpperCase() === normalizedCode;
      }) || '';
    }

    function colorOption(code) {
      var select = document.getElementById('variant-selector');
      if (!select) return null;
      return Array.prototype.slice.call(select.options).find(function (option) {
        var title = option.getAttribute('data-variant-title') || option.textContent || '';
        return String(title).trim().toUpperCase() === String(code || '').trim().toUpperCase();
      }) || null;
    }

    function colorLabel(code) {
      var colors = window.PartifyTouchupColorDetails;
      if (colors && activeVehicle()) return colors.label(state.colorDetails, code);
      var option = colorOption(code);
      var label = option ? String(option.textContent || '').split('+')[0].trim() : '';
      return label && label.toUpperCase() !== String(code || '').toUpperCase()
        ? code + ' - ' + label
        : code;
    }

    function professionalEntries() {
      var requestedCode = codeForMethod();
      var code = isVinMatch(requestedCode) ? requestedCode : mapKeyForCode(requestedCode);
      return (getMap()[code] || []).filter(function (entry) {
        return entry && entry.id && !!canonicalSize(entry.size) && !isVinMatch(entry.code || entry.paintCode || entry.variantTitle || entry.title);
      });
    }

    function availableEntry(entry) {
      return entry && entry.id && entry.available !== false;
    }

    function entriesForSize(size) {
      var wanted = canonicalSize(size);
      return professionalEntries().filter(function (entry) {
        return canonicalSize(entry.size) === wanted;
      });
    }

    function orderedSizes() {
      return SIZE_ORDER.filter(function (size) {
        return entriesForSize(size).some(availableEntry);
      });
    }

    function packageOrder() {
      var order = Array.isArray(window.__touchupPackageOrder)
        ? window.__touchupPackageOrder.slice()
        : [];
      ['Paint Only', 'Essential', 'Preferred'].forEach(function (name) {
        if (order.indexOf(name) === -1) order.push(name);
      });
      return order;
    }

    function orderedPackages(size) {
      var names = entriesForSize(size).filter(availableEntry).map(function (entry) {
        return String(entry.packageName || '').trim();
      });
      var unique = names.filter(function (name, index) {
        return name && names.indexOf(name) === index;
      });
      var order = packageOrder();
      return unique.sort(function (left, right) {
        var leftIndex = order.indexOf(left);
        var rightIndex = order.indexOf(right);
        if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
        if (leftIndex === -1) return 1;
        if (rightIndex === -1) return -1;
        return leftIndex - rightIndex;
      });
    }

    function selectedSize() {
      var sizes = orderedSizes();
      if (sizes.indexOf(state.selectedSize) !== -1) return state.selectedSize;
      if (sizes.indexOf(initialSize) !== -1) return initialSize;
      return sizes[0] || '';
    }

    function selectedPackage(size) {
      var packages = orderedPackages(size);
      if (packages.indexOf(state.selectedPackage) !== -1) return state.selectedPackage;
      if (packages.indexOf(initialPackage) !== -1) return initialPackage;
      return packages.find(function (name) { return normalize(name) === 'essential'; }) || packages[0] || '';
    }

    function entryForSelection(size, packageName) {
      return entriesForSize(size).find(function (entry) {
        return availableEntry(entry) && String(entry.packageName || '') === String(packageName || '');
      }) || null;
    }

    function selectedCodeIsTricoat() {
      var code = codeForMethod();
      return (Array.isArray(window.__partifyTricoatCodes) ? window.__partifyTricoatCodes : []).some(function (tricoatCode) {
        return paintCodeMatches(code, tricoatCode);
      });
    }

    function configuredSystemImage(size, packageName) {
      var sizeAttribute = {
        'Half Pint': 'half-pint',
        'Pint': 'pint',
        'Quart': 'quart',
        'Gallon': 'gallon',
      }[canonicalSize(size)];
      var packageAttribute = normalize(packageName);
      if (!sizeAttribute || (packageAttribute !== 'essential' && packageAttribute !== 'preferred')) return '';
      return root.getAttribute('data-prof-' + sizeAttribute + '-' + packageAttribute + '-image') || '';
    }

    function systemImageSource(size, packageName, entry) {
      var sizeKey = normalize(size);
      var packageKey = normalize(packageName);
      var tricoatImages = window.__partifyTricoatImages && window.__partifyTricoatImages[sizeKey];
      if (selectedCodeIsTricoat() && tricoatImages && tricoatImages[packageKey]) {
        return tricoatImages[packageKey];
      }
      return configuredSystemImage(size, packageName) ||
        (entry && entry.productImage) ||
        fallbackImages[packageKey] ||
        '';
    }

    function syncGalleryHero(size, packageName, entry) {
      var source = systemImageSource(size, packageName, entry);
      var hero = document.querySelector('[data-prof-gallery] [data-gallery-figure][data-gallery-index="0"]');
      var image = hero && hero.querySelector('img');
      if (!source || !image) return;
      if (image.getAttribute('src') !== source) image.setAttribute('src', source);
      image.removeAttribute('srcset');
      image.setAttribute('alt', 'Partify ' + canonicalSize(size) + ' ' + String(packageName || '') +
        (selectedCodeIsTricoat()
          ? ' ready-to-spray system with basecoat, midcoat, clearcoat, and quarter-size clearcoat activator.'
          : ' ready-to-spray system with basecoat, clearcoat, and quarter-size clearcoat activator.'));
    }

    function sizeMeta(size) {
      return {
        'Half Pint': 'Small panel and spot refinishing',
        'Pint': 'Expanded or multi-panel work',
        'Quart': 'Large refinishing projects',
        'Gallon': 'Complete repaints and high-material work',
      }[size] || 'Professional spray-gun application';
    }

    function sizeVolume(size) {
      return {
        'Half Pint': '8 fl oz',
        'Pint': '16 fl oz',
        'Quart': '32 fl oz',
        'Gallon': '128 fl oz',
      }[size] || '';
    }

    function packageMeta(name) {
      var meta = {
        paintonly: {
          description: 'Factory-matched color for prepared spray-gun refinishing.',
          inclusions: ['Matched color paint'],
          note: 'Prep and finish products not included',
        },
        essential: {
          description: 'Matched color plus a two-can 2K clearcoat system for professional refinishing.',
          inclusions: ['Matched color paint', 'Clearcoat can', 'Quarter-size activator can'],
          note: 'Clear + quarter-size activator included',
        },
        preferred: {
          description: 'Adds primer when the prepared repair exposes substrate.',
          inclusions: ['Primer', 'Matched color paint', 'Clearcoat can', 'Quarter-size activator can'],
          note: 'Primer + clear + quarter-size activator',
        },
      };
      return meta[normalize(name)] || {
        description: 'A mapped Professional Paint system for your selected factory color.',
        inclusions: ['Factory-matched Professional Paint'],
        note: '',
      };
    }

    function lowestPriceForSize(size) {
      var prices = entriesForSize(size).filter(availableEntry).map(function (entry) {
        return Number(String(entry.price || '').replace(/,/g, ''));
      }).filter(function (price) { return !isNaN(price); });
      return prices.length ? Math.min.apply(Math, prices) : null;
    }

    function syncDisplayedPrice(entry) {
      const block = priceBlock;
      const prefix = block && block.querySelector('[data-prof-price-prefix]');
      const value = block && block.querySelector('[data-prof-price-value]');
      const context = block && block.querySelector('[data-prof-price-context]');
      const exactPrice = entry ? formatMoney(entry.price) : '';

      if (block && prefix && value && context) {
        if (entry && exactPrice) {
          block.setAttribute('data-prof-price-state', 'exact');
          prefix.textContent = '';
          value.textContent = exactPrice;
          context.textContent = state.selectedSize + ' / ' + state.selectedPackage + ' / exact mapped price';
        } else {
          block.setAttribute('data-prof-price-state', 'starting');
          prefix.textContent = 'From';
          if (startingPriceText) value.textContent = startingPriceText;
          context.textContent = 'Prices vary by paint size and system. Match your factory color for exact pricing.';
        }
      }

      const legacyText = entry && exactPrice
        ? exactPrice
        : startingPriceText ? 'From ' + startingPriceText : '';
      if (!legacyText) return;
      ['#price-display', '#price-display-small-screen', '#price-display-sticky', '#price-display-a2c'].forEach(function (selector) {
        const target = document.querySelector(selector);
        if (target) target.textContent = legacyText;
      });
    }

    function activeVehicle() {
      try {
        var list = typeof window.getSearchTerms === 'function'
          ? window.getSearchTerms()
          : JSON.parse(localStorage.getItem('searchTerms') || '[]');
        return Array.isArray(list) && list.length ? list[0] : null;
      } catch (error) {
        return null;
      }
    }

    function activeVinString() {
      var vehicle = activeVehicle() || {};
      var manualInput = root.querySelector('[data-prof-manual-vin]');
      return normalizeVin(
        vehicle.vin || vehicle.VIN || vehicle.vehicleVin || vehicle.vehicleVIN ||
        vehicle.vinNumber || vehicle.vin_string || vehicle.vinString ||
        vehicle.decodedVin || vehicle.decodedVIN || (manualInput && manualInput.value) || state.vin || ''
      );
    }

    function syncManualVinProperty() {
      var vin = activeVinString();
      Array.prototype.slice.call(document.querySelectorAll('#product-form, form.product-form, form[action*="/cart/add"]')).forEach(function (form) {
        if (!form || vin.length !== 17) return;
        var input = form.querySelector('input[name="properties[VIN]"]') || getOrCreateHiddenInput(form, 'input[name="properties[VIN]"]', 'properties[VIN]');
        input.value = vin;
      });
    }

    function resolvedVehiclePaintCode() {
      var vehicle = activeVehicle() || {};
      var finderData = window.PartifyTouchupFinderData || {};
      var raw = typeof finderData.getResolvedPaintCode === 'function'
        ? finderData.getResolvedPaintCode(vehicle)
        : vehicle.paintCode || vehicle.paint_code || vehicle.troublesome_format || '';
      raw = String(raw || '').trim();
      if (raw.indexOf(' / ') !== -1) raw = raw.split(' / ').pop().trim();
      if (!raw || isVinMatch(raw) || normalizeVin(raw) === activeVinString()) return '';
      return mapKeyForCode(raw);
    }

    function localTwoToneCodes(vehicle) {
      var config = window.__PARTIFY_TWO_TONE_PAINT_RULES;
      var resolver = window.PartifyTwoToneRuleResolver;
      if (!config || !Array.isArray(config.rules) || !resolver || typeof resolver.selectRule !== 'function') return [];
      var selected = resolver.selectRule(config.rules, vehicle || {});
      return selected && Array.isArray(selected.codes) ? selected.codes : [];
    }

    function loadVehiclePaintContext(vehicle) {
      var request = ++paintContextRequest;
      state.colorDetails = null;
      if (!vehicle) {
        state.allowedCodes = null;
        state.allowedCodesLoading = false;
        state.paintDataAvailable = null;
        state.twoToneCodes = [];
        state.toneChoice = '';
        state.toneArea = '';
        scheduleRender();
        return Promise.resolve();
      }
      state.allowedCodes = null;
      state.allowedCodesLoading = true;
      state.paintDataAvailable = null;
      scheduleRender();
      var finderData = window.PartifyTouchupFinderData || {};
      if (typeof finderData.getColorDetails === 'function') {
        Promise.resolve(finderData.getColorDetails(vehicle)).then(function (details) {
          if (request !== paintContextRequest) return;
          state.colorDetails = details;
          var colors = window.PartifyTouchupColorDetails;
          if (colors) {
            colors.updateOptions(content.querySelector('[data-prof-color]'), details);
            colors.renderPreview(content.querySelector('[data-touchup-color-preview]'), details, state.colorCode);
          }
        }).catch(function () {});
      }
      var kitPromise = typeof finderData.ensureKitProducts === 'function'
        ? Promise.resolve(finderData.ensureKitProducts(vehicle)).catch(function () { return false; })
        : Promise.resolve(false);
      var allowedPromise = typeof finderData.getAllowedCodes === 'function'
        ? finderData.getAllowedCodes(vehicle)
        : typeof window.__partifyTouchupFetchAllowedCodes === 'function'
          ? window.__partifyTouchupFetchAllowedCodes(vehicle)
          : Promise.resolve(null);
      var twoTonePromise = typeof finderData.getTwoToneCodes === 'function'
        ? finderData.getTwoToneCodes(vehicle)
        : Promise.resolve(localTwoToneCodes(vehicle));
      return kitPromise.then(function () {
        return Promise.all([allowedPromise, twoTonePromise]);
      }).then(function (results) {
        if (request !== paintContextRequest) return;
        state.paintDataAvailable = Array.isArray(results[0]) && (results[0].partifyVehiclePaintDataAvailable === true || results[0].length > 0);
        state.allowedCodes = Array.isArray(results[0]) ? uniquePaintCodes(results[0]) : null;
        state.allowedCodesLoading = false;
        state.twoToneCodes = uniquePaintCodes(results[1]);
        if (!state.twoToneCodes.length) {
          state.toneChoice = '';
          state.toneArea = '';
        }
        if (state.method === 'manual' && state.colorCode && manualCodes().indexOf(state.colorCode) === -1) {
          state.colorCode = '';
          state.selectedSize = initialSize;
          state.selectedPackage = initialPackage;
        }
        scheduleRender();
      }).catch(function () {
        if (request !== paintContextRequest) return;
        state.allowedCodes = null;
        state.allowedCodesLoading = false;
        state.paintDataAvailable = false;
        state.twoToneCodes = uniquePaintCodes(localTwoToneCodes(vehicle));
        scheduleRender();
      });
    }

    function twoToneEligible() {
      return state.twoToneCodes.length > 0;
    }

    function secondaryToneSelected() {
      return twoToneEligible() && state.toneChoice === 'two' && state.toneArea === 'secondary';
    }

    function toneSelectionReady() {
      if (!twoToneEligible()) return true;
      if (state.toneChoice === 'one') return true;
      return state.toneChoice === 'two' && (state.toneArea === 'primary' || state.toneArea === 'secondary');
    }

    function vinPaintDataAvailable() {
      return state.paintDataAvailable === true;
    }

    function vinAvailabilityStatus() {
      if (state.method !== 'vin' || state.vin.length !== 17 || state.resolvedVin !== state.vin) return '';
      if (state.allowedCodesLoading) return 'Checking Shopify paint availability for this vehicle…';
      if (!vinPaintDataAvailable()) {
        return 'Touch-up paint is not available for this vehicle because we do not have a Shopify paint-code mapping for it. Match by VIN and Add to Cart are disabled.';
      }
      return resolvedVehiclePaintCode() ? '' : paintCodeRequiredMessage;
    }

    function vinResultMarkup() {
      if (state.method !== 'vin' || state.vin.length !== 17 || state.resolvedVin !== state.vin || state.allowedCodesLoading || !vinPaintDataAvailable()) return '';
      var code = codeForMethod();
      if (!code) return '';
      var label = isVinMatch(code) ? 'Match by VIN' : code;
      return '<p class="tupp-inline-configurator__vin-result" data-prof-vin-result role="status"><span>VIN: ' + escapeHtml(activeVinString()) + '</span><span>Paint code: ' + escapeHtml(label) + '</span></p>';
    }

    function vinDecodeSuccessMessage(vehicle) {
      var finderData = window.PartifyTouchupFinderData || {};
      var code = typeof finderData.getResolvedPaintCode === 'function'
        ? finderData.getResolvedPaintCode(vehicle || activeVehicle())
        : (vehicle && (vehicle.paintCode || vehicle.paint_code || vehicle.troublesome_format)) || '';
      code = String(code || '').trim();
      if (code && !isVinMatch(code)) {
        return 'VIN decoded successfully. Your vehicle was added to Garage. Paint code ' + code.toUpperCase() + ' was found.';
      }
      return paintCodeRequiredMessage;
    }

    function manualCodes() {
      var codes = Object.keys(getMap()).filter(function (code) { return !isVinMatch(code); }).sort();
      var filter = secondaryToneSelected() ? state.twoToneCodes : state.allowedCodes;
      if (!Array.isArray(filter)) return codes;
      return codes.filter(function (code) {
        return filter.some(function (allowed) { return paintCodeMatches(code, allowed); });
      });
    }

    function vehicleLabel(vehicle) {
      if (!vehicle) return '';
      return String(
        vehicle.ymm ||
        vehicle.YMM ||
        vehicle.vehicle ||
        [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel].filter(Boolean).join(' ')
      ).trim();
    }

    function openGarage() {
      if (window.GarageV2 && typeof window.GarageV2.open === 'function') {
        window.GarageV2.open();
        return;
      }
      if (typeof window.toggleGaragePopup === 'function') {
        window.toggleGaragePopup();
        return;
      }
      var button = document.getElementById('easyBtn') || document.getElementById('easyBtnMobile');
      if (button && typeof button.click === 'function') button.click();
    }

    function garageMarkup(vehicle) {
      var label = vehicleLabel(vehicle);
      if (!label) {
        return '<button type="button" class="tupp-inline-configurator__garage tupp-inline-configurator__garage--empty" data-prof-garage>' +
          '<span class="tupp-inline-configurator__garage-icon" aria-hidden="true">+</span>' +
          '<span><strong>Add a vehicle to your garage</strong><small>Choose it once, then keep it ready for future color-matched repairs.</small></span>' +
          '<span aria-hidden="true">&rarr;</span>' +
        '</button>';
      }
      return '<div class="tupp-inline-configurator__garage is-saved">' +
        '<span class="tupp-inline-configurator__garage-icon" aria-hidden="true">&#10003;</span>' +
        '<span><small>Shopping for</small><strong>' + escapeHtml(label) + '</strong></span>' +
        '<button type="button" data-prof-garage>Change</button>' +
      '</div>';
    }

    function getOrCreateHiddenInput(form, selector, name) {
      var input = form.querySelector(selector);
      if (input) return input;
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
      return input;
    }

    function clickNativeTouchupButton(containerId, label) {
      var buttons = Array.prototype.slice.call(
        document.querySelectorAll('#' + containerId + ' .touchup-option-button')
      );
      var button = buttons.find(function (item) {
        return String(item.textContent || '').trim().toLowerCase() === String(label || '').trim().toLowerCase();
      });
      if (button && !button.disabled) button.click();
    }

    function syncExistingControls() {
      var code = codeForMethod();
      var activeVin = activeVinString();
      var matchByVinRequired = state.method === 'vin' && isVinMatch(code);
      var size = selectedSize();
      var packageName = selectedPackage(size);
      var entry = entryForSelection(size, packageName);
      if (!code || !size || !packageName || !entry) return false;

      var select = document.getElementById('variant-selector');
      var option = colorOption(code);
      if (select && option) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      window.setTimeout(function () {
        clickNativeTouchupButton('touchup-size-buttons', size);
        clickNativeTouchupButton('touchup-package-buttons', packageName);
        syncDisplayedPrice(entry);
      }, 0);

      window.__touchupSelectedKitEntry = entry;
      window.__touchupSelectedKitVariantId = String(entry.id);
      window.__touchupSelectedKitSku = entry.sku || '';

      Array.prototype.slice.call(document.querySelectorAll(
        '#product-form, form.product-form, form[action*="/cart/add"]'
      )).forEach(function (form) {
        if (!form) return;
        var kitInput = getOrCreateHiddenInput(form, 'input[data-touchup-kit-variant-id]', 'id');
        kitInput.setAttribute('data-touchup-kit-variant-id', 'true');
        kitInput.value = String(entry.id);

        var vinInput = form.querySelector('input[name="properties[VIN]"]');
        if (activeVin.length === 17) {
          vinInput = vinInput || getOrCreateHiddenInput(form, 'input[name="properties[VIN]"]', 'properties[VIN]');
          vinInput.value = activeVin;
        } else if (vinInput) {
          vinInput.value = '';
        }
      });

      window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = matchByVinRequired && activeVin.length === 17;
      window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_MISSING = false;
      window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = state.method === 'manual';
      return true;
    }

    function methodReady() {
      if (!toneSelectionReady()) return false;
      if (state.method === 'vin') return state.vin.length === 17 && state.resolvedVin === state.vin && !state.resolvingVin && !state.allowedCodesLoading && vinPaintDataAvailable() && !!codeForMethod();
      if (state.method === 'plate') return state.plate.length >= 2 && !!state.plateState;
      return !!codeForMethod();
    }

    function setMethod(method) {
      if (secondaryToneSelected() && method !== 'manual') method = 'manual';
      state.method = method;
      state.userChoseMethod = true;
      state.plateMessage = '';
      if (method !== 'manual') state.colorCode = '';
      window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = method === 'manual';
      window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = false;
      state.selectedSize = initialSize;
      state.selectedPackage = initialPackage;
      scheduleRender();
    }

    function renderToneFields() {
      if (!twoToneEligible()) return '';
      var choices = [
        { value: 'one', title: 'One factory color', copy: 'Use VIN, plate, or the vehicle\'s factory paint-code list.' },
        { value: 'two', title: 'Two factory colors', copy: 'Choose the main or accent color you need to repair.' },
      ];
      var markup = '<fieldset class="tupp-tone-choice"><legend>How many factory paint colors does your vehicle have?</legend><div class="tupp-tone-choice__grid">' + choices.map(function (choice) {
        var selected = state.toneChoice === choice.value;
        return '<button type="button" class="tupp-tone-option' + (selected ? ' is-selected' : '') + '" data-prof-tone="' + choice.value + '" aria-pressed="' + String(selected) + '"><strong>' + choice.title + '</strong><span>' + choice.copy + '</span></button>';
      }).join('') + '</div></fieldset>';
      if (state.toneChoice === 'two') {
        markup += '<fieldset class="tupp-tone-choice"><legend>Which color are you touching up?</legend><div class="tupp-tone-choice__grid">' + [
          { value: 'primary', title: 'Main / primary color', copy: 'The color covering most of the vehicle.' },
          { value: 'secondary', title: 'Accent / secondary color', copy: 'The contrasting second factory color.' },
        ].map(function (choice) {
          var selected = state.toneArea === choice.value;
          return '<button type="button" class="tupp-tone-option' + (selected ? ' is-selected' : '') + '" data-prof-tone-area="' + choice.value + '" aria-pressed="' + String(selected) + '"><strong>' + choice.title + '</strong><span>' + choice.copy + '</span></button>';
        }).join('') + '</div></fieldset>';
      }
      return markup;
    }

    function renderMethodFields() {
      if (state.method === 'vin') {
        return '<label class="tupp-inline-configurator__field"><span>Vehicle identification number</span>' +
          '<input data-prof-vin value="' + escapeAttribute(state.vin) + '" maxlength="17" inputmode="text" autocomplete="off" placeholder="17-character VIN"></label>';
      }

      if (state.method === 'plate') {
        var stateCodes = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
        return '<div class="tupp-inline-configurator__field-row">' +
          '<label class="tupp-inline-configurator__field"><span>License plate</span><input data-prof-plate value="' + escapeAttribute(state.plate) + '" autocomplete="off" placeholder="ABC1234"></label>' +
          '<label class="tupp-inline-configurator__field"><span>State</span><select data-prof-plate-state><option value="">State</option>' +
          stateCodes.map(function (code) {
            return '<option value="' + code + '"' + (state.plateState === code ? ' selected' : '') + '>' + code + '</option>';
          }).join('') + '</select></label></div>' +
          '<p class="tupp-inline-configurator__helper">We use the plate and state to find the VIN, then match the factory color.</p>' +
          (state.plateMessage ? '<p class="tupp-inline-configurator__status">' + escapeHtml(state.plateMessage) + '</p>' : '');
      }

      var codes = manualCodes();
      var placeholder = state.allowedCodesLoading ? 'Loading paint codes…' : codes.length ? 'Select factory paint code' : 'No paint codes found for this vehicle';
      var options = ['<option value="">' + placeholder + '</option>'].concat(codes.map(function (code) {
        return '<option value="' + escapeAttribute(code) + '"' + (code === state.colorCode ? ' selected' : '') + '>' + escapeHtml(colorLabel(code)) + '</option>';
      }));
      return '<label class="tupp-inline-configurator__field"><span>Factory paint code</span><select data-prof-color' + (state.allowedCodesLoading || !codes.length ? ' disabled' : '') + '>' + options.join('') + '</select></label>' +
        '<figure class="touchup-color-preview" data-touchup-color-preview hidden></figure>' +
        '<p class="tupp-inline-configurator__helper">' + (secondaryToneSelected() ? 'Choose the factory code for the accent / secondary color you are repairing.' : 'Paint codes are limited to the selected vehicle. Find yours on the driver-side door jamb.') + '</p>' +
        (state.colorCode ? manualVinRecommendationMarkup() : '');
    }

    function manualVinRecommendationMarkup() {
      var vin = activeVinString();
      return '<div class="tupp-inline-configurator__manual-vin" data-prof-manual-vin-prompt>' +
        '<strong>Add VIN for Better Color Match <em>(Recommended)</em></strong>' +
        '<label class="tupp-inline-configurator__field"><span>Vehicle Identification Number</span>' +
          '<span class="tupp-inline-configurator__manual-vin-row"><input data-prof-manual-vin name="properties[VIN]" value="' + escapeAttribute(vin) + '" maxlength="17" inputmode="text" autocomplete="off" placeholder="e.g. 18UYA31581L000000"></span></label>' +
        '<p class="tupp-inline-configurator__helper">Enter all 17 characters. Your selected paint code stays in place, and the VIN is automatically sent with the order so our paint team can refine the color match.</p>' +
        (state.manualVinSaved ? '<p class="tupp-inline-configurator__manual-vin-success" role="status">VIN added to this color match.</p>' : '') +
      '</div>';
    }

    function renderSizeCards(colorReady) {
      var sizes = colorReady ? orderedSizes() : [];
      if (colorReady && !sizes.length) {
        return '<p class="tupp-inline-configurator__empty">No Professional Paint size is mapped to this color yet. Try VIN matching or contact a paint specialist.</p>';
      }
      var activeSize = colorReady ? selectedSize() : '';
      if (activeSize) state.selectedSize = activeSize;
      return (!colorReady
          ? '<p class="prof-size-grid__status">Match your vehicle or factory color to unlock exact availability and pricing.</p>'
          : '') +
        '<div class="prof-size-grid" role="group" aria-label="Choose professional paint size">' +
        SIZE_ORDER.map(function (size) {
          var available = colorReady && sizes.indexOf(size) !== -1;
          var selected = available && size === activeSize;
          var price = available ? lowestPriceForSize(size) : null;
          return '<button type="button" class="prof-size-choice' + (selected ? ' is-selected' : '') + (!colorReady ? ' is-awaiting-color' : '') + '"' +
            (available ? ' data-prof-size="' + escapeAttribute(size) + '" aria-pressed="' + String(selected) + '"' : ' disabled aria-disabled="true"') + '>' +
            '<strong>' + escapeHtml(size) + '</strong>' +
            '<span class="prof-size-choice__volume">' + escapeHtml(sizeVolume(size)) + '</span>' +
            '<span class="prof-size-choice__use">' + escapeHtml(sizeMeta(size)) + '</span>' +
            (colorReady ? '<small>' + (available && price != null ? 'From ' + escapeHtml(formatMoney(price)) : 'Unavailable for this color') + '</small>' : '') +
          '</button>';
        }).join('') +
      '</div>';
    }

    function packageImage(name, entry) {
      var source = systemImageSource(state.selectedSize || initialSize, name, entry);
      if (!source) return '<span class="tupp-package-choice__media" aria-hidden="true"></span>';
      return '<span class="tupp-package-choice__media"><img src="' + escapeAttribute(source) + '" alt="" loading="lazy"></span>';
    }

    function packageCard(name, entry, disabled) {
      var meta = packageMeta(name);
      var selected = !disabled && name === state.selectedPackage;
      var recommended = normalize(name) === 'essential';
      var attributes = disabled
        ? ' disabled aria-disabled="true"'
        : ' data-prof-package="' + escapeAttribute(name) + '" aria-pressed="' + String(selected) + '"';
      return '<button type="button" class="tupp-package-choice' + (selected ? ' is-selected' : '') + '"' + attributes + '>' +
        packageImage(name, entry) +
        '<span class="tupp-package-choice__copy">' +
          '<span class="tupp-package-choice__topline"><span>' + escapeHtml(name) + '</span>' +
            (recommended ? '<span class="tupp-package-choice__recommendation">Recommended</span>' : '') +
          '</span>' +
          '<span class="tupp-package-choice__description">' + escapeHtml(meta.description) + '</span>' +
          '<span class="tupp-package-choice__includes">' + meta.inclusions.map(function (item) {
            return '<span>' + escapeHtml(item) + '</span>';
          }).join('') + '</span>' +
          '<span class="tupp-package-choice__footer"><span>' + escapeHtml(meta.note) + '</span>' +
            (!disabled && entry ? '<strong>' + escapeHtml(formatMoney(entry.price)) + '</strong>' : '') +
          '</span>' +
        '</span>' +
      '</button>';
    }

    function renderPackageCards(colorReady, size) {
      if (!colorReady || !size || state.method === 'plate') {
        return '<div class="tupp-inline-configurator__packages" aria-label="Professional Paint systems available after color and size selection">' +
          ['Essential', 'Preferred', 'Paint Only'].map(function (name) { return packageCard(name, null, true); }).join('') +
        '</div><p class="tupp-inline-configurator__helper">Match the vehicle and choose a size to unlock exact system pricing.</p>';
      }
      var packages = orderedPackages(size);
      if (!packages.length) {
        return '<p class="tupp-inline-configurator__empty">No paint system is available for this color and size. Choose another available size or contact a paint specialist.</p>';
      }
      state.selectedPackage = selectedPackage(size);
      return '<div class="tupp-inline-configurator__packages">' + packages.map(function (name) {
        return packageCard(name, entryForSelection(size, name), false);
      }).join('') + '</div>';
    }

    function selectionText(ready, entry) {
      if (!ready || !entry) return 'Complete your professional paint setup';
      var color = state.method === 'manual' ? colorLabel(state.colorCode) : 'Factory match';
      return color + ' - ' + state.selectedSize + ' - ' + state.selectedPackage;
    }

    function render() {
      var previousPreview = content.querySelector('[data-touchup-color-preview]');
      var ready = methodReady();
      var toneReady = toneSelectionReady();
      var colorReady = ready && state.method !== 'plate';
      var size = colorReady ? selectedSize() : '';
      if (size) state.selectedSize = size;
      var packageName = size ? selectedPackage(size) : '';
      if (packageName) state.selectedPackage = packageName;
      var entry = size && packageName ? entryForSelection(size, packageName) : null;
      var addReady = ready && (state.method === 'plate' || entry) && !state.resolvingPlate && !state.adding;
      var addLabel = state.adding ? 'Adding&hellip;' : state.resolvingPlate ? 'Finding your color&hellip;' : state.method === 'plate' ? 'Find my factory color' : 'Add to cart';
      var addButtonMarkup = '<button type="button" class="tupp-inline-configurator__atc" data-prof-add' + (addReady ? '' : ' disabled') + '>' + addLabel + ' <span aria-hidden="true">&rarr;</span></button>';

      content.innerHTML =
        garageMarkup(activeVehicle()) +
        '<div class="tupp-inline-configurator__step">' +
          '<div class="tupp-inline-configurator__step-heading"><span>01</span><div><h3>Find your factory color</h3><p>VIN matching gives the strongest factory-color path.</p></div></div>' +
          renderToneFields() +
          '<div class="tupp-inline-configurator__methods" role="radiogroup" aria-label="Choose a color matching method">' +
            ['vin', 'plate', 'manual'].map(function (method) {
              var labels = { vin: ['VIN match', 'Most accurate'], plate: ['License plate', 'VIN lookup'], manual: ['Paint code', 'I know it'] };
              var disabled = !toneReady || (secondaryToneSelected() && method !== 'manual');
              return '<button type="button" class="tupp-match-method' + (state.method === method ? ' is-selected' : '') + '" data-prof-method="' + method + '" role="radio" aria-checked="' + String(state.method === method) + '"' + (disabled ? ' disabled' : '') + '><strong>' + labels[method][0] + '</strong><span>' + labels[method][1] + '</span></button>';
            }).join('') +
          '</div>' +
          (toneReady ? renderMethodFields() : '<p class="tupp-inline-configurator__helper">Choose the factory-color setup above to continue.</p>') +
          vinResultMarkup() +
          (state.method !== 'plate' && state.plateMessage ? '<p class="tupp-inline-configurator__status" role="status">' + escapeHtml(state.plateMessage) + '</p>' : '') +
          (vinAvailabilityStatus() ? '<p class="tupp-inline-configurator__status" role="status">' + escapeHtml(vinAvailabilityStatus()) + '</p>' : '') +
        '</div>' +
        '<div class="tupp-inline-configurator__step prof-size-step' + (colorReady ? '' : ' is-disabled') + '">' +
          '<div class="tupp-inline-configurator__step-heading"><span>02</span><div><h3>Choose your paint size</h3><p>Pick the mapped volume that fits the scale of the refinishing job.</p></div></div>' +
          renderSizeCards(colorReady) +
        '</div>' +
        '<div class="tupp-inline-configurator__step' + (colorReady && size ? '' : ' is-disabled') + '">' +
          '<div class="tupp-inline-configurator__step-heading"><span>03</span><div><h3>Choose your professional paint system</h3><p>Essential and Preferred include a two-can 2K clearcoat system: one clearcoat can plus one quarter-size activator can.</p></div></div>' +
          renderPackageCards(colorReady, size) +
        '</div>' +
        '<div class="tupp-inline-configurator__cta-row">' +
          '<div><span class="tupp-inline-configurator__selection-label">Your selection</span><strong>' + escapeHtml(selectionText(ready, entry)) + '</strong></div>' +
          addButtonMarkup +
        '</div>';
      syncGalleryHero(size || state.selectedSize || initialSize, packageName || state.selectedPackage || initialPackage, entry);
      syncDisplayedPrice(entry);
      var colors = window.PartifyTouchupColorDetails;
      var preview = content.querySelector('[data-touchup-color-preview]');
      if (previousPreview && preview) {
        preview.replaceWith(previousPreview);
        preview = previousPreview;
      }
      if (colors) colors.renderPreview(preview, state.colorDetails, state.colorCode);
      bind();
    }

    function scheduleRender() {
      window.clearTimeout(renderTimer);
      renderTimer = window.setTimeout(render, 0);
    }

    function syncVinToGarage() {
      var vin = state.vin;
      if (vin.length !== 17) return Promise.resolve(false);
      if (state.resolvedVin === vin) return Promise.resolve(true);
      var sync = window.PartifyTouchupVinGarageSync;
      if (!sync || typeof sync.decodeAndActivate !== 'function') {
        state.resolvedVin = vin;
        state.resolvingVin = false;
        state.plateMessage = '';
        scheduleRender();
        return Promise.resolve(true);
      }
      state.resolvingVin = true;
      state.plateMessage = 'Decoding VIN and updating your Garage\u2026';
      render();
      return Promise.resolve(sync.decodeAndActivate(vin)).then(function (vehicle) {
        if (state.vin !== vin) return false;
        state.resolvingVin = false;
        state.resolvedVin = vin;
        state.plateMessage = vinDecodeSuccessMessage(vehicle);
        return loadVehiclePaintContext(vehicle || activeVehicle()).then(function () {
          if (state.vin !== vin) return false;
          scheduleRender();
          return true;
        });
      }).catch(function () {
        if (state.vin !== vin) return false;
        state.resolvingVin = false;
        state.resolvedVin = '';
        state.plateMessage = 'We could not decode that VIN. Check it and try again.';
        scheduleRender();
        return false;
      });
    }

    function resolvePlate() {
      if (state.resolvingPlate) return;
      if (typeof window.fetchBumpersLicenseToVin !== 'function') {
        state.plateMessage = 'Plate lookup is still loading. Try again, or use your VIN or paint code.';
        scheduleRender();
        return;
      }
      state.resolvingPlate = true;
      state.plateMessage = '';
      render();
      Promise.resolve(window.fetchBumpersLicenseToVin(state.plateState, state.plate))
        .then(function (result) {
          var vin = normalizeVin(result && result.vin);
          if (vin.length !== 17) throw new Error('No VIN found');
          state.method = 'vin';
          state.vin = vin;
          state.resolvedVin = '';
          state.userChoseMethod = true;
          state.colorCode = '';
          state.selectedSize = initialSize;
          state.selectedPackage = initialPackage;
          state.plateMessage = '';
          state.resolvingPlate = false;
          syncVinToGarage();
        })
        .catch(function () {
          state.resolvingPlate = false;
          state.plateMessage = 'We could not resolve that plate. Check it, or use your VIN or paint code.';
          scheduleRender();
        });
    }

    function cartProperties(form, entry, packageName) {
      var properties = {};
      if (form) {
        new FormData(form).forEach(function (value, key) {
          var match = key.match(/^properties\[(.+)\]$/);
          if (match && value != null && String(value).length) properties[match[1]] = value;
        });
      }
      var resolvedCode = codeForMethod();
      properties['Touch-Up Color'] = resolvedCode && !isVinMatch(resolvedCode) ? resolvedCode : 'Match by VIN';
      properties['Kit Format'] = entry.size || selectedSize();
      properties['Kit Package'] = entry.packageName || packageName;
      properties['Kit Product'] = entry.productTitle || document.querySelector('h1')?.textContent?.trim() || document.title;
      var activeVehicleLabel = vehicleLabel(activeVehicle());
      if (activeVehicleLabel) properties.Vehicle = activeVehicleLabel;
      else delete properties.Vehicle;
      delete properties.EasySearch;
      delete properties.Fits;
      var activeVin = activeVinString();
      if (activeVin.length === 17) properties.VIN = activeVin;
      else delete properties.VIN;
      return properties;
    }

    function vehicleIdentity(vehicle) {
      if (!vehicle) return '';
      return normalize([
        vehicle.year, vehicle.make, vehicle.model, vehicle.submodel || vehicle.trim,
        vehicle.engine, vehicle.vin, vehicle.plate,
      ].filter(Boolean).join(' ') || vehicle.ymm || vehicle.YMM || vehicle.vehicle);
    }

    function confirmQuantity(payload, properties) {
      var confirmation = window.PartifyCartQuantityConfirmation;
      if (!confirmation || typeof confirmation.confirmAdd !== 'function') return Promise.resolve(true);
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : { items: [] }; })
        .catch(function () { return { items: [] }; })
        .then(function (cart) {
          if (!cart.items || !cart.items.length) return true;
          return confirmation.confirmAdd({
            cart: cart,
            items: [{ id: payload.id, variant_id: payload.id, quantity: 1, title: properties['Kit Product'], properties: properties, partifyQuantityRuleKey: 'touch_up_paint_kit' }],
          }).then(function (result) { return result.confirmed !== false; });
        });
    }

    function submitNativeForm() {
      if (state.adding) return;
      if (state.method === 'vin' && (state.allowedCodesLoading || !vinPaintDataAvailable())) {
        state.plateMessage = vinAvailabilityStatus() || 'We could not verify Shopify paint data for this vehicle. Match by VIN and Add to Cart are disabled.';
        scheduleRender();
        return;
      }
      var size = selectedSize();
      var packageName = selectedPackage(size);
      var entry = entryForSelection(size, packageName);
      if (!entry || !entry.id) {
        state.plateMessage = 'That paint system is still loading. Choose the size and package again.';
        scheduleRender();
        return;
      }
      state.adding = true;
      /* The mapped kit variant is authoritative. Calling the legacy control
       * mirror here can synchronously replace this configurator and discard
       * the customer's pending direct-cart request. */
      var form;
      var properties;
      var payload;
      try {
        form = document.getElementById('product-form') || document.querySelector('form.product-form, form[action*="/cart/add"]');
        properties = cartProperties(form, entry, packageName);
        payload = { id: Number(entry.id), quantity: 1, properties: properties };
      } catch (error) {
        state.adding = false;
        state.plateMessage = error.message || 'We could not prepare this paint system for the cart.';
        scheduleRender();
        return;
      }
      var button = content.querySelector('[data-prof-add]');
      var idleLabel = button ? button.innerHTML : '';
      if (button) { button.disabled = true; button.textContent = 'Adding\u2026'; }

      confirmQuantity(payload, properties)
        .then(function (confirmed) {
          if (!confirmed) {
            state.adding = false;
            scheduleRender();
            return null;
          }
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });
        })
        .then(function (response) {
          if (response === null) return;
          if (!response.ok) {
            return response.json().catch(function () { return {}; }).then(function (body) {
              throw new Error(body.description || body.message || 'Unable to add this professional paint package to the cart.');
            });
          }
          window.location.assign('/cart');
        })
        .catch(function (error) {
          state.adding = false;
          state.plateMessage = error.message || 'We could not add this package. Please try again.';
          if (button) { button.disabled = false; button.innerHTML = idleLabel; }
          scheduleRender();
          window.alert(state.plateMessage);
        });
    }

    function addToCart() {
      if (state.method === 'plate') {
        resolvePlate();
        return;
      }
      if (state.method === 'vin' && state.resolvedVin !== state.vin) {
        syncVinToGarage().then(function (ready) { if (ready) addToCart(); });
        return;
      }
      submitNativeForm();
    }

    function bind() {
      if (root.__profConfiguratorDelegated) return;
      root.__profConfiguratorDelegated = true;

      root.addEventListener('click', function (event) {
        var target = event.target && event.target.closest ? event.target : null;
        if (!target) return;

        var method = target.closest('[data-prof-method]');
        if (method) {
          setMethod(method.getAttribute('data-prof-method'));
          return;
        }

        var tone = target.closest('[data-prof-tone]');
        if (tone) {
          state.toneChoice = tone.getAttribute('data-prof-tone') || '';
          state.toneArea = '';
          state.colorCode = '';
          state.selectedSize = initialSize;
          state.selectedPackage = initialPackage;
          scheduleRender();
          return;
        }

        var toneArea = target.closest('[data-prof-tone-area]');
        if (toneArea) {
          state.toneArea = toneArea.getAttribute('data-prof-tone-area') || '';
          state.colorCode = '';
          state.selectedSize = initialSize;
          state.selectedPackage = initialPackage;
          if (state.toneArea === 'secondary') setMethod('manual');
          else scheduleRender();
          return;
        }

        var size = target.closest('[data-prof-size]');
        if (size) {
          state.selectedSize = canonicalSize(size.getAttribute('data-prof-size'));
          state.selectedPackage = '';
          state.selectedPackage = selectedPackage(state.selectedSize);
          syncExistingControls();
          scheduleRender();
          return;
        }

        var packageButton = target.closest('[data-prof-package]');
        if (packageButton) {
          state.selectedPackage = packageButton.getAttribute('data-prof-package') || '';
          syncExistingControls();
          scheduleRender();
          return;
        }

        if (target.closest('[data-prof-add]')) {
          addToCart();
          return;
        }

        if (target.closest('[data-prof-garage]')) openGarage();
      });

      root.addEventListener('change', function (event) {
        var target = event.target;
        if (!target) return;
        if (target.matches('[data-prof-color]')) {
          state.userChoseMethod = true;
          state.colorCode = target.value;
          state.manualVinSaved = false;
          window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = true;
          window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = false;
          state.selectedSize = initialSize;
          state.selectedPackage = initialPackage;
          scheduleRender();
        } else if (target.matches('[data-prof-plate-state]')) {
          state.plateState = target.value;
          state.plateMessage = '';
          scheduleRender();
        }
      });

      root.addEventListener('input', function (event) {
        var target = event.target;
        if (!target) return;
        if (target.matches('[data-prof-vin]')) {
          var wasVinReady = state.vin.length === 17;
          state.userChoseMethod = true;
          state.vin = normalizeVin(target.value);
          state.resolvedVin = '';
          state.plateMessage = '';
          window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = false;
          target.value = state.vin;
          state.selectedSize = initialSize;
          state.selectedPackage = initialPackage;
          if (state.vin.length === 17) syncVinToGarage();
          else if (wasVinReady) scheduleRender();
        } else if (target.matches('[data-prof-manual-vin]')) {
          state.vin = normalizeVin(target.value);
          state.manualVinSaved = false;
          target.value = state.vin;
          var submit = root.querySelector('[data-prof-manual-vin-submit]');
          var prompt = root.querySelector('[data-prof-manual-vin-prompt]');
          var success = prompt && prompt.querySelector('.tupp-inline-configurator__manual-vin-success');
          if (state.vin.length === 17) {
            state.manualVinSaved = true;
            syncManualVinProperty();
            if (submit) { submit.textContent = 'VIN added'; submit.disabled = true; }
            if (prompt && !success) prompt.insertAdjacentHTML('beforeend', '<p class="tupp-inline-configurator__manual-vin-success" role="status">VIN added to this color match.</p>');
          } else {
            if (submit) { submit.textContent = 'Add VIN'; submit.disabled = true; }
            if (success) success.remove();
          }
        } else if (target.matches('[data-prof-plate]')) {
          state.plate = normalizePlate(target.value);
          target.value = state.plate;
          state.plateMessage = '';
          var addButton = root.querySelector('[data-prof-add]');
          if (addButton && state.method === 'plate') {
            addButton.disabled = state.plate.length < 2 || !state.plateState || state.resolvingPlate;
          }
        }
      });
    }

    function hydrateVehicle(force) {
      var vehicle = activeVehicle();
      if (!vehicle) return false;
      var nextKey = vehicleIdentity(vehicle);
      var changed = nextKey !== state.vehicleKey;
      if (state.userChoseMethod && !force && !changed) return false;
      state.vehicleKey = nextKey;
      if (changed) state.userChoseMethod = false;
      var vin = normalizeVin(vehicle.vin);
      var paintCode = String(vehicle.paintCode || vehicle.paint_code || '').trim();
      if (vin.length === 17) {
        state.method = 'vin';
        state.vin = vin;
        state.resolvedVin = vin;
        state.colorCode = '';
      } else if (paintCode && mapKeyForCode(paintCode)) {
        state.method = 'manual';
        state.colorCode = mapKeyForCode(paintCode);
      }
      return changed;
    }

    function refreshFromGarage() {
      var vehicle = activeVehicle();
      if (hydrateVehicle(false)) {
        state.selectedSize = initialSize;
        state.selectedPackage = initialPackage;
        state.toneChoice = '';
        state.toneArea = '';
      }
      loadVehiclePaintContext(vehicle);
      scheduleRender();
    }

    function waitForData(attempt) {
      window.clearTimeout(dataRetryTimer);
      if (Object.keys(getMap()).length) {
        hydrateVehicle();
        render();
        loadVehiclePaintContext(activeVehicle());
        return;
      }
      if (attempt === 0) render();
      if (attempt >= DATA_RETRY_LIMIT) {
        render();
        return;
      }
      dataRetryTimer = window.setTimeout(function () { waitForData(attempt + 1); }, DATA_RETRY_DELAY);
    }

    function expandProfessionalCatalog(attempt) {
      if (catalogExpansionStarted || !makeKey || !collectionHandle) return;
      if (typeof window.__partifyTouchupLoadKitProductsForCollection !== 'function') {
        if (attempt >= 24) return;
        window.setTimeout(function () { expandProfessionalCatalog(attempt + 1); }, 250);
        return;
      }
      catalogExpansionStarted = true;
      Promise.resolve(window.__partifyTouchupLoadKitProductsForCollection(collectionHandle, makeKey))
        .then(function (loaded) {
          if (!loaded) return;
          state.selectedSize = canonicalSize(state.selectedSize) || initialSize;
          state.selectedPackage = state.selectedPackage || initialPackage;
          render();
        })
        .catch(function () {
          // The already-rendered Shopify product remains the safe fallback.
        });
    }

    window.addEventListener('partify:vehicle:changed', refreshFromGarage);
    window.addEventListener('searchTermsUpdated', refreshFromGarage);
    document.addEventListener('partify:vehicle:changed', refreshFromGarage);
    document.addEventListener('partify:touchup:selection-complete', scheduleRender);
    document.addEventListener('variantsUpdated', function () {
      window.setTimeout(function () { waitForData(0); }, 0);
    });
    waitForData(0);
    expandProfessionalCatalog(0);
  }

  function bootAll() {
    Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR)).forEach(boot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();
  document.addEventListener('shopify:section:load', bootAll);

  if (typeof MutationObserver === 'function' && document.documentElement) {
    if (window.__profConfiguratorObserver) window.__profConfiguratorObserver.disconnect();
    window.__profConfiguratorObserver = new MutationObserver(bootAll);
    window.__profConfiguratorObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
